import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import './LotForm.css';
import '../Common/CardActions.css';

const LotForm = ({
  lot,
  formData,
  setFormData,
  onSave,
  onCancel,
  isViewMode = false,
  onFormChange,
  isLotAttached = () => false,
  getAuctionInfo = () => '—',
  vatLocked = false,
  isConfirmOpen = false,
  onExitEdit,
  initialLotNumber = '',
}) => {
  const [initialFormData, setInitialFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [creditors, setCreditors] = useState([]);
  const [creditorsLoading, setCreditorsLoading] = useState(false);
  const [classifiers, setClassifiers] = useState([]);
  const [classifiersLoading, setClassifiersLoading] = useState(false);
  const [showClassifierModal, setShowClassifierModal] = useState(false);
  const [showClassifierDiscardConfirm, setShowClassifierDiscardConfirm] = useState(false);
  const [classifierSearch, setClassifierSearch] = useState('');
  const [classifierDraftSelected, setClassifierDraftSelected] = useState([]);
  const [classifierStickyIndex, setClassifierStickyIndex] = useState(-1);
  const [isHydratingLotFields, setIsHydratingLotFields] = useState(false);
  const classifierPickerRef = useRef(null);
  const classifierFieldPointerRef = useRef({ x: 0, y: 0, scrollTop: 0, moved: false });
  const classifierListRef = useRef(null);
  const classifierRowRefs = useRef([]);
  const classifierSearchInputRef = useRef(null);
  const initLotSnapshotRef = useRef(null);
  const [creditorModal, setCreditorModal] = useState({ isOpen: false, mode: null, item: null }); // create|edit
  const [creditorForm, setCreditorForm] = useState({ name: '' });
  const [creditorHasChanges, setCreditorHasChanges] = useState(false);
  // В конкурсной процедуре должника значение НДС устанавливается автоматически и блокируется.
  const vatLockedOverride = Boolean(lot?.vat_locked || vatLocked);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (showClassifierModal) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        return;
      }
      if (creditorModal?.isOpen || isConfirmOpen) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (!isViewMode && onExitEdit) {
        onExitEdit();
        return false;
      }
      onCancel();
      return false;
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel, creditorModal?.isOpen, isConfirmOpen, isViewMode, onExitEdit, showClassifierModal]);

  useEffect(() => {
    let newFormData;

    if (lot) {
      newFormData = {
        lot_number: lot.lot_number ?? '',
        name: lot.name || '',
        description: lot.description || '',
        start_price: lot.start_price || '',
        vat_text: lot.vat_text || '',
        cadastral_number: lot.cadastral_number || '',
        location: lot.location || '',
        category: lot.category || 'real_estate',
        efrsb_classifiers: Array.isArray(lot.efrsb_classifiers) ? lot.efrsb_classifiers : [],
        pledge_creditor_satisfaction: !!lot.pledge_creditor_satisfaction,
        pledge_creditors: Array.isArray(lot.pledge_creditors) ? lot.pledge_creditors : [],
        public_offer_settings: (lot.public_offer_settings && typeof lot.public_offer_settings === 'object') ? lot.public_offer_settings : {},
        public_offer_periods: Array.isArray(lot.public_offer_periods) ? lot.public_offer_periods : [],
        public_offer_periods_filled_once: !!lot.public_offer_periods_filled_once,
      };
    } else {
      const presetLotNumber = String(initialLotNumber ?? '').trim();
      newFormData = {
        lot_number: presetLotNumber || '',
        name: '',
        description: '',
        start_price: '',
        vat_text: '',
        cadastral_number: '',
        location: '',
        category: 'real_estate',
        efrsb_classifiers: [],
        pledge_creditor_satisfaction: false,
        pledge_creditors: [],
        public_offer_settings: {},
        public_offer_periods: [],
        public_offer_periods_filled_once: false,
      };
    }

    if (vatLockedOverride) {
      newFormData.vat_text = 'НДС не облагается';
    }

    initLotSnapshotRef.current = newFormData;
    setIsHydratingLotFields(Boolean(lot));
    setFormData(newFormData);
    setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
    setValidationErrors({});
  }, [lot, setFormData, vatLockedOverride, initialLotNumber]);

  useEffect(() => {
    if (!isHydratingLotFields) return;
    const snapshot = initLotSnapshotRef.current;
    if (!snapshot) {
      setIsHydratingLotFields(false);
      return;
    }
    const lotNumberReady = String(formData?.lot_number ?? '') === String(snapshot?.lot_number ?? '');
    const nameReady = String(formData?.name ?? '') === String(snapshot?.name ?? '');
    if (lotNumberReady && nameReady) {
      setIsHydratingLotFields(false);
    }
  }, [formData?.lot_number, formData?.name, isHydratingLotFields]);

  useEffect(() => {
    if (!showClassifierModal) return;
    const closeClassifierDropdown = () => {
      setShowClassifierModal(false);
      setShowClassifierDiscardConfirm(false);
      requestAnimationFrame(() => {
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
      });
    };
    const hasClassifierSelectionChanges = () => {
      const normalizeIds = (ids) => Array.from(new Set(
        (ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))
      )).sort((a, b) => a - b);
      const currentIds = normalizeIds(formData?.efrsb_classifiers || []);
      const draftIds = normalizeIds(classifierDraftSelected || []);
      return currentIds.length !== draftIds.length || currentIds.some((id, idx) => id !== draftIds[idx]);
    };
    const onWindowKeyDownCapture = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      if (event.nativeEvent?.stopImmediatePropagation) event.nativeEvent.stopImmediatePropagation();
      if (showClassifierDiscardConfirm) {
        setShowClassifierDiscardConfirm(false);
        return;
      }
      if (hasClassifierSelectionChanges()) {
        setShowClassifierDiscardConfirm(true);
        return;
      }
      closeClassifierDropdown();
    };
    const onMouseDown = (event) => {
      if (!classifierPickerRef.current) return;
      if (classifierPickerRef.current.contains(event.target)) return;
      if (hasClassifierSelectionChanges()) {
        setShowClassifierDiscardConfirm(true);
        return;
      }
      closeClassifierDropdown();
    };
    window.addEventListener('keydown', onWindowKeyDownCapture, { capture: true });
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onWindowKeyDownCapture, { capture: true });
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [showClassifierModal, showClassifierDiscardConfirm, classifierDraftSelected, formData?.efrsb_classifiers]);

  useEffect(() => {
    if (!initialFormData || !onFormChange) return;
    const changed = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    onFormChange(changed);
  }, [formData, initialFormData, onFormChange]);

  useEffect(() => {
    if (!vatLockedOverride) return;
    setFormData((prev) => {
      if (!prev) return prev;
      if (prev.vat_text === 'НДС не облагается') return prev;
      return { ...prev, vat_text: 'НДС не облагается' };
    });
  }, [vatLockedOverride, lot, setFormData]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const normalizeStartPriceInput = (value) => {
    const raw = String(value ?? '').replace(',', '.');
    if (!raw) return '';
    const cleaned = raw.replace(/[^\d.]/g, '');
    const hasDot = cleaned.includes('.');
    const [wholeRaw = '', ...fractionParts] = cleaned.split('.');
    const whole = wholeRaw.replace(/^0+(?=\d)/, '');
    const fraction = fractionParts.join('').slice(0, 2);
    if (hasDot) return `${whole || '0'}.${fraction}`;
    return whole;
  };

  const formatGroupedStartPriceInput = (value) => {
    const normalized = normalizeStartPriceInput(value);
    if (!normalized) return '';
    const hasDot = normalized.includes('.');
    const [whole = '', fraction = ''] = normalized.split('.');
    const wholeDigits = (whole || '0').replace(/^0+(?=\d)/, '');
    const groupedWhole = (wholeDigits || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    if (!hasDot) return groupedWhole;
    return `${groupedWhole}.${fraction}`;
  };

  const normalizeLotNumberInput = (value) => {
    const raw = String(value ?? '');
    const cleaned = raw.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
    return cleaned;
  };

  const loadCreditors = useCallback(async () => {
    try {
      setCreditorsLoading(true);
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getCreditors(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setCreditors(all);
    } catch (err) {
      console.error('Ошибка загрузки кредиторов:', err);
    } finally {
      setCreditorsLoading(false);
    }
  }, []);

  const loadClassifiers = useCallback(async () => {
    try {
      setClassifiersLoading(true);
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getClassifiers(`?page=${page}`);
        const chunk = resp.data?.results || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 500) break;
      }
      all.sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')));
      setClassifiers(all);
    } catch (err) {
      console.error('Ошибка загрузки классификаторов ЕФРСБ:', err);
    } finally {
      setClassifiersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClassifiers();
  }, [loadClassifiers]);

  useEffect(() => {
    if (formData?.pledge_creditor_satisfaction) {
      loadCreditors();
    }
  }, [formData?.pledge_creditor_satisfaction, loadCreditors]);

  const selectedCreditorIds = Array.isArray(formData?.pledge_creditors) ? formData.pledge_creditors : [];
  const selectedCreditorDetails = Array.isArray(lot?.pledge_creditors_details) ? lot.pledge_creditors_details : [];
  const selectedCreditorsForDisplay = (() => {
    const byId = new Map();
    selectedCreditorDetails.forEach((c) => {
      if (c?.id) byId.set(c.id, c);
    });
    (creditors || []).forEach((c) => {
      if (c?.id) byId.set(c.id, c);
    });
    return selectedCreditorIds.map((id) => byId.get(id)).filter(Boolean);
  })();

  const openCreateCreditor = () => {
    setCreditorForm({ name: '' });
    setCreditorHasChanges(false);
    setCreditorModal({ isOpen: true, mode: 'create', item: null });
  };

  const openEditCreditor = (item) => {
    setCreditorForm({ name: item?.name || '' });
    setCreditorHasChanges(false);
    setCreditorModal({ isOpen: true, mode: 'edit', item });
  };

  const closeCreditorModal = () => {
    setCreditorModal({ isOpen: false, mode: null, item: null });
    setCreditorForm({ name: '' });
    setCreditorHasChanges(false);
  };

  const saveCreditor = async ({ exit } = {}) => {
    try {
      let saved = null;
      if (creditorModal.mode === 'edit' && creditorModal.item?.id) {
        const resp = await apiService.updateCreditor(creditorModal.item.id, creditorForm);
        saved = resp.data;
        setCreditors((prev) => (prev || []).map((c) => (c.id === saved.id ? saved : c)));
      } else {
        const resp = await apiService.createCreditor(creditorForm);
        saved = resp.data;
        setCreditors((prev) => [saved, ...(prev || [])]);
      }
      setCreditorHasChanges(false);
      if (saved?.id) {
        setFormData((prev) => {
          const prevIds = Array.isArray(prev?.pledge_creditors) ? prev.pledge_creditors : [];
          if (prevIds.includes(saved.id)) return prev;
          return { ...(prev || {}), pledge_creditors: [...prevIds, saved.id] };
        });
      }
      if (exit) closeCreditorModal();
      else openEditCreditor(saved);
    } catch (err) {
      console.error('Ошибка сохранения кредитора:', err);
    }
  };

  const validateForm = () => {
    const errors = {};

    const lotNumber = Number.parseInt(String(formData?.lot_number || '').trim(), 10);
    if (!lotNumber || lotNumber <= 0) {
      errors.lot_number = 'Номер лота должен быть целым числом больше 0';
    }

    if (!formData?.name || !formData.name.trim()) {
      errors.name = 'Обязательное поле';
    }

    const price = parseFloat(formData?.start_price);
    if (!price || price <= 0) {
      errors.start_price = 'Начальная цена должна быть больше 0';
    } else if (!/^\d+([.,]\d{1,2})?$/.test(String(formData?.start_price || ''))) {
      errors.start_price = 'Допустимо не более 2 знаков после запятой';
    }

    return errors;
  };

  const runSave = async (shouldExit = false) => {
    if (isViewMode || isSaving) return false;
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    if (!onSave) return false;

    setIsSaving(true);
    try {
      const payload = { ...(formData || {}) };
      const result = await onSave(payload, { exit: shouldExit });

      if (result === false) {
        return false;
      }

      const snapshot = JSON.parse(JSON.stringify(payload));
      setInitialFormData(snapshot);
      setValidationErrors({});
      if (onFormChange) {
        onFormChange(false);
      }
      return result;
    } catch (error) {
      console.error('Ошибка при сохранении лота:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSave(false).catch(() => {});
  };

  const getCategoryText = (category) => {
    const categories = {
      real_estate: 'Недвижимость',
      transport: 'Транспорт',
      equipment: 'Оборудование',
      debt: 'Дебиторка',
      other: 'Прочее',
    };
    return categories[category] || category;
  };

  const classifierById = new Map((classifiers || []).map((item) => [String(item?.id), item]));
  const classifierIdsSource = (isHydratingLotFields && Array.isArray(lot?.efrsb_classifiers))
    ? lot.efrsb_classifiers
    : formData?.efrsb_classifiers;
  const selectedClassifierIds = Array.isArray(classifierIdsSource)
    ? classifierIdsSource.map((id) => Number(id)).filter((id) => Number.isFinite(id))
    : [];
  const selectedClassifierDetails = selectedClassifierIds
    .map((id) => classifierById.get(String(id)))
    .filter(Boolean);
  const hasFormClassifierSelection = Array.isArray(classifierIdsSource);
  const hasSelectedClassifierIds = selectedClassifierIds.length > 0;
  const fallbackSelectedClassifiers = Array.isArray(lot?.efrsb_classifiers_details)
    ? lot.efrsb_classifiers_details
    : [];
  const classifiersForDisplay = (() => {
    if (!hasFormClassifierSelection) return fallbackSelectedClassifiers;
    if (selectedClassifierDetails.length > 0) return selectedClassifierDetails;
    if (hasSelectedClassifierIds) {
      return fallbackSelectedClassifiers;
    }
    return [];
  })();
  const hasPendingClassifierResolve = hasSelectedClassifierIds && classifiersForDisplay.length === 0;
  const isClassifierSelectionEmpty = classifiersForDisplay.length === 0 && !hasPendingClassifierResolve;
  const effectiveVatText = (() => {
    const currentVat = formData?.vat_text;
    if (
      isHydratingLotFields
      && (currentVat === undefined || currentVat === null || String(currentVat) === '')
      && String(lot?.vat_text || '') !== ''
    ) {
      return lot.vat_text;
    }
    return currentVat || '';
  })();

  const rootParentKey = '__root__';
  const classifierChildrenByParent = classifiers.reduce((acc, item) => {
    const parentRaw = item?.parent;
    const parentKey = (parentRaw === null || parentRaw === undefined || parentRaw === '') ? rootParentKey : String(parentRaw);
    if (!acc.has(parentKey)) acc.set(parentKey, []);
    acc.get(parentKey).push(item);
    return acc;
  }, new Map());
  classifierChildrenByParent.forEach((items) => {
    items.sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')));
  });
  const nonLeafClassifierIds = new Set();
  classifierChildrenByParent.forEach((items, parentKey) => {
    if (parentKey === rootParentKey) return;
    if (!Array.isArray(items) || items.length === 0) return;
    const parentId = Number(parentKey);
    if (Number.isFinite(parentId) && parentId > 0) {
      nonLeafClassifierIds.add(parentId);
    }
  });

  const normalizeClassifierSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9]+/gi, ' ')
    .trim();
  const classifierSearchPrepared = normalizeClassifierSearchText(classifierSearch);
  const classifierParentById = new Map();
  const classifierMatchedIds = new Set();
  (classifiers || []).forEach((item) => {
    const itemId = String(item?.id ?? '');
    if (!itemId) return;
    const parentRaw = item?.parent;
    const parentId = (parentRaw === null || parentRaw === undefined || parentRaw === '') ? '' : String(parentRaw);
    classifierParentById.set(itemId, parentId);
    if (!classifierSearchPrepared) {
      classifierMatchedIds.add(itemId);
      return;
    }
    const text = normalizeClassifierSearchText(`${String(item?.code || '')} ${String(item?.name || '')}`);
    if (text.includes(classifierSearchPrepared)) {
      classifierMatchedIds.add(itemId);
    }
  });

  const classifierVisibleIds = (() => {
    if (!classifierSearchPrepared) {
      return new Set((classifiers || []).map((item) => String(item?.id ?? '')).filter(Boolean));
    }
    const out = new Set();
    (classifiers || []).forEach((item) => {
      const itemId = String(item?.id ?? '');
      if (!itemId || !classifierMatchedIds.has(itemId)) return;
      let cursor = itemId;
      const seen = new Set();
      while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        out.add(cursor);
        cursor = classifierParentById.get(cursor) || '';
      }
    });
    return out;
  })();

  const openClassifierModal = () => {
    setClassifierDraftSelected(selectedClassifierIds);
    setClassifierSearch('');
    setClassifierStickyIndex(-1);
    setShowClassifierDiscardConfirm(false);
    setShowClassifierModal(true);
  };

  const handleClassifierFieldMouseDown = (event) => {
    if (event.button !== 0) return;
    classifierFieldPointerRef.current = {
      x: event.clientX,
      y: event.clientY,
      scrollTop: event.currentTarget.scrollTop || 0,
      moved: false,
    };
  };

  const handleClassifierFieldMouseMove = (event) => {
    const state = classifierFieldPointerRef.current;
    if (!state) return;
    if (Math.abs(event.clientX - state.x) > 4 || Math.abs(event.clientY - state.y) > 4) {
      state.moved = true;
    }
  };

  const handleClassifierFieldClick = (event) => {
    const state = classifierFieldPointerRef.current || {};
    const currentScrollTop = event.currentTarget.scrollTop || 0;
    const scrollChanged = Math.abs(currentScrollTop - Number(state.scrollTop || 0)) > 1;
    const moved = !!state.moved;
    classifierFieldPointerRef.current.moved = false;
    if (moved || scrollChanged) return;
    openClassifierModal();
  };

  const toggleClassifierChecked = (id) => {
    const numId = Number(id);
    if (!numId) return;
    if (nonLeafClassifierIds.has(numId)) return;
    setClassifierDraftSelected((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(numId)) return current.filter((item) => item !== numId);
      return [...current, numId];
    });
  };

  const applyClassifierSelection = () => {
    const normalized = Array.from(new Set(
      (classifierDraftSelected || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && !nonLeafClassifierIds.has(id))
    ));
    updateFormData('efrsb_classifiers', normalized);
    setShowClassifierModal(false);
    setShowClassifierDiscardConfirm(false);
    requestAnimationFrame(() => {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    });
  };

  const handleClassifierDropdownKeyDownCapture = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent?.stopImmediatePropagation) event.nativeEvent.stopImmediatePropagation();
    const normalizeIds = (ids) => Array.from(new Set(
      (ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))
    )).sort((a, b) => a - b);
    const currentIds = normalizeIds(formData?.efrsb_classifiers || []);
    const draftIds = normalizeIds(classifierDraftSelected || []);
    const hasChanges = currentIds.length !== draftIds.length || currentIds.some((id, idx) => id !== draftIds[idx]);
    if (hasChanges) {
      setShowClassifierDiscardConfirm(true);
      return;
    }
    setShowClassifierModal(false);
    setShowClassifierDiscardConfirm(false);
    requestAnimationFrame(() => {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    });
  };

  const renderedClassifierItems = [];
  const collectVisibleRows = (parentId = rootParentKey, level = 0) => {
    const items = classifierChildrenByParent.get(parentId) || [];
    items.forEach((item) => {
      const itemId = Number(item?.id || 0);
      if (!itemId) return;
      if (classifierSearchPrepared && !classifierVisibleIds.has(String(itemId))) return;
      renderedClassifierItems.push({ item, level });
      collectVisibleRows(String(itemId), level + 1);
    });
  };
  collectVisibleRows(rootParentKey, 0);

  useEffect(() => {
    if (!showClassifierModal) return;
    const container = classifierListRef.current;
    if (!container) return;

    const updateStickyByScroll = () => {
      const scrollTop = container.scrollTop;
      let nextIndex = -1;
      for (let i = 0; i < renderedClassifierItems.length; i += 1) {
        const node = classifierRowRefs.current[i];
        if (!node) continue;
        const bottom = node.offsetTop + node.offsetHeight;
        if (bottom > scrollTop + 1) {
          nextIndex = i;
          break;
        }
      }
      if (nextIndex === -1 && renderedClassifierItems.length > 0) {
        nextIndex = renderedClassifierItems.length - 1;
      }
      setClassifierStickyIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    };

    updateStickyByScroll();
    container.addEventListener('scroll', updateStickyByScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', updateStickyByScroll);
    };
  }, [showClassifierModal, classifierSearch, renderedClassifierItems.length]);

  useEffect(() => {
    if (!showClassifierModal) return;
    const timer = setTimeout(() => {
      const input = classifierSearchInputRef.current;
      if (input && typeof input.focus === 'function') {
        input.focus();
        if (typeof input.select === 'function') input.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [showClassifierModal]);

  const stickyItem = classifierStickyIndex >= 0 ? renderedClassifierItems[classifierStickyIndex]?.item : null;
  const activeHierarchyItem = stickyItem || renderedClassifierItems[0]?.item || null;
  const hierarchyTrail = (() => {
    if (!activeHierarchyItem) return [];
    const trail = [];
    const seen = new Set();
    let cursor = activeHierarchyItem;
    while (cursor) {
      const cursorId = Number(cursor?.id || 0);
      if (!cursorId || seen.has(cursorId)) break;
      seen.add(cursorId);
      trail.unshift(cursor);
      const parentId = Number(cursor?.parent || 0);
      if (!parentId) break;
      cursor = classifierById.get(String(parentId)) || null;
    }
    return trail;
  })();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(price);
  };

  const formatDateTimeDisplay = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    const normalized = raw.replace('T', ' ');
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
    return normalized;
  };

  return (
    <div className="aot-lot-form">
      <div className="form-header">
        <h3>{lot ? `Лот "${lot.name}"` : 'Создание нового лота'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Наименование лота:</label>
          <input
            type="text"
            value={formData?.name || ''}
            readOnly={isViewMode}
            onChange={(e) => updateFormData('name', e.target.value)}
            className={validationErrors.name ? 'error' : ''}
            placeholder="Введите наименование лота"
            required={!isViewMode}
          />
          {validationErrors.name && <div className="error-text">{validationErrors.name}</div>}
        </div>

        <div className="form-group">
          <label>Описание:</label>
          <textarea
            value={formData?.description || ''}
            readOnly={isViewMode}
            onChange={(e) => updateFormData('description', e.target.value)}
            placeholder="Введите описание лота"
            rows={4}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Номер лота:</label>
            <input
              type={isViewMode ? 'text' : 'number'}
              min={isViewMode ? undefined : '1'}
              step={isViewMode ? undefined : '1'}
              value={formData?.lot_number ?? ''}
              readOnly={isViewMode}
              onChange={(e) => updateFormData('lot_number', normalizeLotNumberInput(e.target.value))}
              className={validationErrors.lot_number ? 'error' : ''}
              placeholder={isViewMode ? '-' : '1'}
              required={!isViewMode}
            />
            {validationErrors.lot_number && <div className="error-text">{validationErrors.lot_number}</div>}
          </div>

          <div className="form-group">
            <label>Начальная цена:</label>
            <input
              type="text"
              inputMode={isViewMode ? undefined : 'decimal'}
              value={isViewMode ? (lot?.start_price ? formatPrice(lot.start_price) : '') : formatGroupedStartPriceInput(formData?.start_price ?? '')}
              readOnly={isViewMode}
              onChange={(e) => updateFormData('start_price', normalizeStartPriceInput(e.target.value))}
              className={validationErrors.start_price ? 'error' : ''}
              placeholder={isViewMode ? '-' : '0.00'}
              required={!isViewMode}
            />
            {validationErrors.start_price && <div className="error-text">{validationErrors.start_price}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group lot-category-field">
            <label>Категория:</label>
            {isViewMode ? (
              <input type="text" value={getCategoryText(formData?.category)} readOnly />
            ) : (
              <select
                value={formData?.category || 'real_estate'}
                onChange={(e) => updateFormData('category', e.target.value)}
              >
                <option value="real_estate">Недвижимость</option>
                <option value="transport">Транспорт</option>
                <option value="equipment">Оборудование</option>
                <option value="debt">Дебиторка</option>
                <option value="other">Прочее</option>
              </select>
            )}
          </div>

          <div className="form-group lot-classifier-field">
            <label>Классификатор ЕФРСБ:</label>
            {isViewMode ? (
              <div className={`classifier-selected-list classifier-selected-list--view ${isClassifierSelectionEmpty ? 'classifier-selected-list--empty' : ''}`}>
                {classifiersForDisplay.length > 0 ? (
                  classifiersForDisplay.map((item) => (
                    <div key={item.id} className="classifier-selected-item">
                      [{item.code}] {item.name}
                    </div>
                  ))
                ) : hasPendingClassifierResolve ? (
                  <div className="classifier-selected-empty">Выбраны классификаторы (загрузка...)</div>
                ) : (
                  <div className="classifier-selected-empty">Не выбрано</div>
                )}
              </div>
            ) : (
              <div className="classifier-picker" ref={classifierPickerRef}>
                <div
                  className={`classifier-selected-list classifier-selected-list--interactive ${isClassifierSelectionEmpty ? 'classifier-selected-list--empty' : ''}`}
                  title="Нажмите для выбора классификаторов"
                  role="button"
                  tabIndex={0}
                  onMouseDown={handleClassifierFieldMouseDown}
                  onMouseMove={handleClassifierFieldMouseMove}
                  onClick={handleClassifierFieldClick}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    openClassifierModal();
                  }}
                >
                  {classifiersForDisplay.length > 0 ? (
                    classifiersForDisplay.map((item) => (
                      <div key={item.id} className="classifier-selected-item">
                        [{item.code}] {item.name}
                      </div>
                    ))
                  ) : hasPendingClassifierResolve ? (
                    <div className="classifier-selected-empty">Выбраны классификаторы (загрузка...)</div>
                  ) : (
                    <div className="classifier-selected-empty">Не выбрано</div>
                  )}
                </div>
                {showClassifierModal && (
                  <div
                    className="classifier-dropdown"
                    onKeyDownCapture={handleClassifierDropdownKeyDownCapture}
                  >
                    <div className="classifier-modal-controls">
                      <input
                        ref={classifierSearchInputRef}
                        type="text"
                        value={classifierSearch}
                        onChange={(e) => setClassifierSearch(e.target.value)}
                        placeholder="Поиск по коду или наименованию..."
                      />
                    </div>
                    <div className="classifier-tree-wrap">
                      {classifiersLoading ? (
                        <div className="classifier-tree-empty">Загрузка...</div>
                      ) : (
                        <>
                          {hierarchyTrail.length > 0 && (
                            <div className="classifier-hierarchy-sticky">
                              {hierarchyTrail.map((item) => (
                                <div key={`sticky-${item.id}`} className="classifier-hierarchy-row">
                                  {item.code} · {item.name}
                                </div>
                              ))}
                            </div>
                          )}
                          <div ref={classifierListRef} className="classifier-tree-scroll">
                            {renderedClassifierItems.map(({ item, level }, index) => {
                              const itemId = Number(item?.id || 0);
                              const isChecked = classifierDraftSelected.includes(itemId);
                              const isNonLeaf = nonLeafClassifierIds.has(itemId);
                              return (
                                <div
                                  key={itemId}
                                  ref={(node) => { classifierRowRefs.current[index] = node; }}
                                  className="classifier-tree-row"
                                  style={{
                                    paddingLeft: `${8 + level * 16}px`,
                                    opacity: isNonLeaf ? 0.75 : 1,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    disabled={isNonLeaf}
                                    onChange={() => toggleClassifierChecked(itemId)}
                                  />
                                  <span className="classifier-tree-code">[{item.code}]</span>
                                  <span className="classifier-tree-name">{item.name}</span>
                                </div>
                              );
                            })}
                          </div>
                          {(!classifiers || classifiers.length === 0) && (
                            <div className="classifier-tree-empty">Нет данных</div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="classifier-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setClassifierDraftSelected([])}
                        title="Снять все выбранные классификаторы"
                      >
                        Очистить выбор
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          const normalizeIds = (ids) => Array.from(new Set(
                            (ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))
                          )).sort((a, b) => a - b);
                          const currentIds = normalizeIds(formData?.efrsb_classifiers || []);
                          const draftIds = normalizeIds(classifierDraftSelected || []);
                          const hasChanges = currentIds.length !== draftIds.length
                            || currentIds.some((id, idx) => id !== draftIds[idx]);
                          if (hasChanges) {
                            setShowClassifierDiscardConfirm(true);
                            return;
                          }
                          setShowClassifierModal(false);
                          setShowClassifierDiscardConfirm(false);
                          requestAnimationFrame(() => {
                            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                              document.activeElement.blur();
                            }
                          });
                        }}
                      >
                        Отмена
                      </button>
                      <button type="button" className="btn btn-primary" onClick={applyClassifierSelection}>
                        Выбрать
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6c757d' }}>
              * поле заполняется для экспорта в ЭТП.
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>НДС:</label>
          {isViewMode ? (
            <input type="text" value={effectiveVatText} readOnly />
          ) : (
            <select
              value={effectiveVatText}
              onChange={(e) => updateFormData('vat_text', e.target.value)}
              disabled={vatLockedOverride}
            >
              <option value="">Не указано</option>
              <option value="НДС не облагается">НДС не облагается</option>
              <option value="в том числе НДС">в том числе НДС</option>
              <option value="Не включая НДС">Не включая НДС</option>
            </select>
          )}
          {vatLockedOverride && (
            <div className="form-value" style={{ marginTop: 6, color: '#6c757d', fontSize: '0.85rem' }}>
              В процедуре конкурсного производства или реализации имущества значение устанавливается автоматически.
            </div>
          )}
        </div>

        <div className="form-group">
          <div className="checkbox-row">
            <input
              id="pledge_creditor_satisfaction"
              type="checkbox"
              checked={!!formData?.pledge_creditor_satisfaction}
              disabled={isViewMode}
              onChange={(e) => {
                const checked = e.target.checked;
                updateFormData('pledge_creditor_satisfaction', checked);
                if (!checked) {
                  updateFormData('pledge_creditors', []);
                }
              }}
            />
            <label htmlFor="pledge_creditor_satisfaction">
              Имущество реализуется с целью удовлетворения требований залогового кредитора
            </label>
          </div>
        </div>

        {!!formData?.pledge_creditor_satisfaction && (
          <div className="form-group">
            <label>Наименование залогового кредитора (можно несколько):</label>
            {isViewMode ? (
              <textarea
                value={
                  (selectedCreditorsForDisplay || []).length > 0 ? selectedCreditorsForDisplay.map((c) => c.name).join('; ') : ''
                }
                readOnly
                rows={2}
                placeholder="-"
              />
            ) : (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  <select
                    value=""
                    onChange={(e) => {
                      const id = parseInt(e.target.value, 10);
                      if (!id) return;
                      updateFormData('pledge_creditors', Array.from(new Set([...selectedCreditorIds, id])));
                    }}
                    disabled={creditorsLoading}
                    style={{ minWidth: 280 }}
                  >
                    <option value="">{creditorsLoading ? 'Загрузка...' : 'Выберите из списка...'}</option>
                    {(creditors || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-secondary" onClick={loadCreditors} disabled={creditorsLoading}>
                    Обновить список
                  </button>
                  <button type="button" className="btn btn-primary" onClick={openCreateCreditor}>
                    Добавить кредитора
                  </button>
                </div>

                {(selectedCreditorsForDisplay || []).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedCreditorsForDisplay.map((c) => (
                      <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div
                          style={{
                            flex: 1,
                            padding: '8px 10px',
                            border: '1px solid #dee2e6',
                            borderRadius: 8,
                            background: '#f8f9fa',
                          }}
                        >
                          {c.name}
                        </div>
                        <div className="card-actions" style={{ justifyContent: 'flex-start' }}>
                          <button
                            type="button"
                            className="card-action-btn card-action-btn--edit"
                            onClick={() => openEditCreditor(c)}
                            title="Изменить"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="card-action-btn card-action-btn--delete"
                            onClick={() => updateFormData('pledge_creditors', selectedCreditorIds.filter((id) => id !== c.id))}
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {lot && (
          <div className="form-group">
            <label>Статус привязки:</label>
            <div className="form-value">
              <span className={`status-badge ${isLotAttached(lot.auction_info) ? 'attached' : 'free'}`}>
                {getAuctionInfo(lot.auction_info)}
              </span>
            </div>
          </div>
        )}

        {(lot?.id || lot?.auction) && (
          (() => {
            const hasFormPeriods = Array.isArray(formData?.public_offer_periods);
            const rows = hasFormPeriods
              ? formData.public_offer_periods
              : (Array.isArray(lot?.public_offer_periods) ? lot.public_offer_periods : []);
            const hasHistory = (typeof formData?.public_offer_periods_filled_once === 'boolean')
              ? formData.public_offer_periods_filled_once
              : !!lot?.public_offer_periods_filled_once;
            if (!hasHistory) return null;
            return (
              <div className="form-group">
                <label>Периоды публичного предложения (только просмотр):</label>
                {rows.length === 0 ? (
                  <div className="form-value">Ранее заполнялось в карточке торгов, сейчас данных нет.</div>
                ) : (
                  <div className="lot-public-offer-readonly-wrap">
                    <table className="lot-public-offer-readonly-table">
                      <thead>
                        <tr>
                          <th>Начало интервала</th>
                          <th>Начало приема заявок</th>
                          <th>Окончание приема заявок</th>
                          <th>Окончание интервала</th>
                          <th>Снижение</th>
                          <th>Задаток</th>
                          <th>Цена</th>
                          <th>Комментарий</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={`lot-po-row-${idx}`}>
                            <td>{formatDateTimeDisplay(row.interval_start_datetime)}</td>
                            <td>{formatDateTimeDisplay(row.applications_start_datetime)}</td>
                            <td>{formatDateTimeDisplay(row.applications_end_datetime)}</td>
                            <td>{formatDateTimeDisplay(row.interval_end_datetime)}</td>
                            <td>{row.reduction_value || '-'}</td>
                            <td>{row.deposit_amount || '-'}</td>
                            <td>{row.interval_price || '-'}</td>
                            <td>{row.comment || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()
        )}

        <div className="form-actions">
          {isViewMode ? (
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Закрыть
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary" disabled={isSaving} onClick={() => runSave(false).catch(() => {})}>
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button type="button" className="btn btn-success" disabled={isSaving} onClick={() => runSave(true).catch(() => {})}>
                {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>
                Отмена
              </button>
            </>
          )}
        </div>
      </form>

      {creditorModal.isOpen && (
        <UniversalModal
          isOpen={creditorModal.isOpen}
          onClose={closeCreditorModal}
          title={creditorModal.mode === 'edit' ? 'Изменить кредитора' : 'Добавить кредитора'}
          hasChanges={creditorHasChanges}
          isViewMode={false}
          onConfirmClose={closeCreditorModal}
        >
          <div style={{ padding: 8 }}>
            <div className="form-group">
              <label>Наименование:</label>
              <input
                type="text"
                value={creditorForm.name}
                onChange={(e) => {
                  setCreditorForm((prev) => ({ ...(prev || {}), name: e.target.value }));
                  setCreditorHasChanges(true);
                }}
                placeholder="Введите наименование кредитора"
              />
            </div>
            <div className="form-actions" style={{ paddingTop: 0, borderTop: 'none', marginTop: 10 }}>
              <button type="button" className="btn btn-primary" onClick={() => saveCreditor({ exit: false })}>
                Сохранить
              </button>
              <button type="button" className="btn btn-success" onClick={() => saveCreditor({ exit: true })}>
                Сохранить и выйти
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeCreditorModal}>
                Отмена
              </button>
            </div>
          </div>
        </UniversalModal>
      )}

      <UnsavedChangesPopover
        isOpen={showClassifierDiscardConfirm}
        title="Подтверждение"
        message="Есть несохраненные изменения в выборе классификаторов. Закрыть без сохранения?"
        onCancel={() => setShowClassifierDiscardConfirm(false)}
        onConfirm={() => {
          setShowClassifierModal(false);
          setShowClassifierDiscardConfirm(false);
          requestAnimationFrame(() => {
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
              document.activeElement.blur();
            }
          });
        }}
        cancelText="Отмена"
        confirmText="Закрыть без сохранения"
        zIndex={14000}
      />
    </div>
  );
};

export default LotForm;
